"use client";

import api from "@/apis";
import { Button } from "@/components/ui/button";
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "@/components/ui/form";
import { Input } from "@/components/ui/input";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Textarea } from "@/components/ui/textarea";
import { sponsorSchema } from "@/schemas/sponsor-schema";
import { zodResolver } from "@hookform/resolvers/zod";
import { Heart, Loader2 } from "lucide-react";
import { useForm } from "react-hook-form";
import { z } from "zod";

export default function BecomeSponsor() {
  const { mutateAsync, isPending } = api.Sponsor.Create.useMutation();

  const form = useForm<z.infer<typeof sponsorSchema>>({
    resolver: zodResolver(sponsorSchema),
    defaultValues: {
      firstName: "",
      lastName: "",
      email: "",
      phone: "",
      address: "",
      zipCode: "",
      city: "",
      country: "",
      sponsorshipType: "monthly",
      monthlyAmount: "30",
      message: "",
    },
  });

  const onSubmit = async (values: z.infer<typeof sponsorSchema>) => {
    try {
      await mutateAsync(values);
      form.reset();
    } catch (error) {
      console.error(error);
    }
  };

  return (
    <main className="min-h-screen">
      {/* Header */}
      <section className="bg-gradient-to-br from-primary/10 to-secondary/10 py-20 px-4 sm:px-6 lg:px-8">
        <div className="max-w-4xl mx-auto">
          <h1 className="text-5xl font-bold text-foreground mb-6">
            Become a Sponsor
          </h1>
          <p className="text-xl text-foreground/80">
            Transform a child&apos;s life through direct sponsorship.
          </p>
        </div>
      </section>

      {/* Content */}
      <section className="py-20 px-4 sm:px-6 lg:px-8">
        <div className="max-w-5xl mx-auto">
          {/* Info Section */}
          <div className="mb-16 p-8 bg-primary/10 rounded-lg">
            <h2 className="text-2xl font-bold mb-4 flex items-center gap-2">
              <Heart className="text-primary" size={28} />
              How Child Sponsorship Works
            </h2>
            <p className="text-foreground/90 leading-relaxed mb-4">
              As a child sponsor, you&apos;ll provide direct support to help a
              specific child in our program receive education, nutrition,
              medical care, and the opportunity to build a better future. Your
              regular sponsorship helps us provide consistent, sustainable
              support.
            </p>
            <p className="text-foreground/90 leading-relaxed">
              We&apos;ll keep you updated on your sponsored child&apos;s progress and will
              send you information about their education and development. Your
              support directly changes their life.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {/* Benefits */}
            <div className="md:col-span-1">
              <h2 className="text-2xl font-bold mb-6">What Your Support Provides</h2>
              <div className="space-y-4">
                <div className="border-l-4 border-primary pl-4">
                  <p className="font-semibold text-foreground">Daily Meals</p>
                  <p className="text-sm text-foreground/80">
                    Three nutritious meals per day
                  </p>
                </div>
                <div className="border-l-4 border-primary pl-4">
                  <p className="font-semibold text-foreground">Education</p>
                  <p className="text-sm text-foreground/80">
                    Quality schooling and vocational training
                  </p>
                </div>
                <div className="border-l-4 border-primary pl-4">
                  <p className="font-semibold text-foreground">Medical Care</p>
                  <p className="text-sm text-foreground/80">
                    Health check-ups and necessary treatment
                  </p>
                </div>
                <div className="border-l-4 border-primary pl-4">
                  <p className="font-semibold text-foreground">Safe Environment</p>
                  <p className="text-sm text-foreground/80">
                    A caring, protective setting
                  </p>
                </div>
                <div className="border-l-4 border-primary pl-4">
                  <p className="font-semibold text-foreground">
                    Long-term Support
                  </p>
                  <p className="text-sm text-foreground/80">
                    We support children through employment
                  </p>
                </div>
              </div>
            </div>

            {/* Form */}
            <div className="md:col-span-2">
              <div className="bg-card p-8 rounded-lg border border-border">
                <Form {...form}>
                  <form
                    onSubmit={form.handleSubmit(onSubmit)}
                    className="space-y-6"
                  >
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      <FormField
                        control={form.control}
                        name="firstName"
                        render={({ field }) => (
                          <FormItem>
                            <FormLabel>First Name *</FormLabel>
                            <FormControl>
                              <Input {...field} />
                            </FormControl>
                            <FormMessage />
                          </FormItem>
                        )}
                      />
                      <FormField
                        control={form.control}
                        name="lastName"
                        render={({ field }) => (
                          <FormItem>
                            <FormLabel>Last Name *</FormLabel>
                            <FormControl>
                              <Input {...field} />
                            </FormControl>
                            <FormMessage />
                          </FormItem>
                        )}
                      />
                    </div>

                    <FormField
                      control={form.control}
                      name="email"
                      render={({ field }) => (
                        <FormItem>
                          <FormLabel>Email *</FormLabel>
                          <FormControl>
                            <Input type="email" {...field} />
                          </FormControl>
                          <FormMessage />
                        </FormItem>
                      )}
                    />

                    <FormField
                      control={form.control}
                      name="phone"
                      render={({ field }) => (
                        <FormItem>
                          <FormLabel>Phone</FormLabel>
                          <FormControl>
                            <Input type="tel" {...field} />
                          </FormControl>
                          <FormMessage />
                        </FormItem>
                      )}
                    />

                    <FormField
                      control={form.control}
                      name="address"
                      render={({ field }) => (
                        <FormItem>
                          <FormLabel>Address *</FormLabel>
                          <FormControl>
                            <Input {...field} />
                          </FormControl>
                          <FormMessage />
                        </FormItem>
                      )}
                    />

                    <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                      <FormField
                        control={form.control}
                        name="zipCode"
                        render={({ field }) => (
                          <FormItem>
                            <FormLabel>Zip Code *</FormLabel>
                            <FormControl>
                              <Input {...field} />
                            </FormControl>
                            <FormMessage />
                          </FormItem>
                        )}
                      />
                      <FormField
                        control={form.control}
                        name="city"
                        render={({ field }) => (
                          <FormItem>
                            <FormLabel>City *</FormLabel>
                            <FormControl>
                              <Input {...field} />
                            </FormControl>
                            <FormMessage />
                          </FormItem>
                        )}
                      />
                      <FormField
                        control={form.control}
                        name="country"
                        render={({ field }) => (
                          <FormItem>
                            <FormLabel>Country *</FormLabel>
                            <FormControl>
                              <Input {...field} />
                            </FormControl>
                            <FormMessage />
                          </FormItem>
                        )}
                      />
                    </div>

                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      <FormField
                        control={form.control}
                        name="sponsorshipType"
                        render={({ field }) => (
                          <FormItem>
                            <FormLabel>Sponsorship Type *</FormLabel>
                            <Select
                              onValueChange={field.onChange}
                              defaultValue={field.value}
                            >
                              <FormControl>
                                <SelectTrigger>
                                  <SelectValue placeholder="Select type" />
                                </SelectTrigger>
                              </FormControl>
                              <SelectContent>
                                <SelectItem value="monthly">
                                  Monthly Sponsorship
                                </SelectItem>
                                <SelectItem value="annual">
                                  Annual Sponsorship
                                </SelectItem>
                                <SelectItem value="one-time">
                                  One-time Donation
                                </SelectItem>
                              </SelectContent>
                            </Select>
                            <FormMessage />
                          </FormItem>
                        )}
                      />
                      <FormField
                        control={form.control}
                        name="monthlyAmount"
                        render={({ field }) => (
                          <FormItem>
                            <FormLabel>Amount (€) *</FormLabel>
                            <Select
                              onValueChange={field.onChange}
                              defaultValue={field.value}
                            >
                              <FormControl>
                                <SelectTrigger>
                                  <SelectValue placeholder="Select amount" />
                                </SelectTrigger>
                              </FormControl>
                              <SelectContent>
                                <SelectItem value="30">€30 / month</SelectItem>
                                <SelectItem value="50">€50 / month</SelectItem>
                                <SelectItem value="75">€75 / month</SelectItem>
                                <SelectItem value="100">€100 / month</SelectItem>
                                <SelectItem value="custom">
                                  Custom amount
                                </SelectItem>
                              </SelectContent>
                            </Select>
                            <FormMessage />
                          </FormItem>
                        )}
                      />
                    </div>

                    <FormField
                      control={form.control}
                      name="message"
                      render={({ field }) => (
                        <FormItem>
                          <FormLabel>Message (Optional)</FormLabel>
                          <FormControl>
                            <Textarea
                              placeholder="Tell us why you want to sponsor a child..."
                              className="resize-none"
                              {...field}
                            />
                          </FormControl>
                          <FormMessage />
                        </FormItem>
                      )}
                    />

                    <Button
                      type="submit"
                      disabled={isPending}
                      className="w-full h-12 text-lg font-semibold"
                    >
                      {isPending ? (
                        <>
                          <Loader2 className="mr-2 h-5 w-5 animate-spin" />
                          Processing...
                        </>
                      ) : (
                        <>
                          <Heart className="mr-2 h-5 w-5" />
                          Become a Sponsor
                        </>
                      )}
                    </Button>
                  </form>
                </Form>
              </div>
            </div>
          </div>
        </div>
      </section>
    </main>
  );
}
